import { useState } from 'react';
import { Mail, RefreshCw, Trash2, UserPlus } from 'lucide-react';
import { ApiError } from '@/api/client';
import { useInvites, useResendInvite, useRevokeInvite, useSendInvite, type SentInvite } from '@/api/invites';
import type { WorkspaceRole } from '@/api/types';
import { useAuth } from '@/app/auth/useAuth';
import { Badge, Button, Card, Input, Modal, Select, useToast } from '@/components/ui';
import { CopyBlock } from '@/components/ui/CopyBlock';
import { formatDate, humanize } from '@/lib/format';
import { ROLE_HELP, assignableRoles } from './helpers';

/** "Invite people" modal: email + role → emailed link, or a link to copy when email isn't set up. */
export function InviteModal({ onClose }: { onClose: () => void }) {
  const { ability } = useAuth();
  const toast = useToast();
  const send = useSendInvite();
  const roles = assignableRoles(ability).filter((r): r is Exclude<WorkspaceRole, 'owner'> => r !== 'owner');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<(typeof roles)[number]>(roles.includes('agent') ? 'agent' : roles[0]!);
  const [sent, setSent] = useState<SentInvite | null>(null);

  if (sent) return <SentModal sent={sent} onClose={onClose} />;

  const fieldErrors = send.error instanceof ApiError ? send.error.fieldErrors : {};
  return (
    <Modal
      open
      onClose={onClose}
      title="Invite a teammate"
      description="They get a link to set their own password and join this workspace."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={send.isPending}
            disabled={!email.trim()}
            onClick={() =>
              send.mutate(
                { email: email.trim(), name: name.trim() || undefined, role },
                {
                  onSuccess: (res) => {
                    if (res.emailed) {
                      toast.success(`Invite sent to ${res.invite.email}`);
                      onClose();
                    } else setSent(res);
                  },
                },
              )
            }
          >
            <Mail className="h-4 w-4" /> Send invite
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Email"
          type="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldErrors.email}
        />
        <Input label="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
        <Select
          label="Role"
          value={role}
          onChange={(e) => setRole(e.target.value as typeof role)}
          hint={ROLE_HELP[role]}
        >
          {roles.map((r) => (
            <option key={r} value={r}>
              {humanize(r)}
            </option>
          ))}
        </Select>
        {send.error && Object.keys(fieldErrors).length === 0 && (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {send.error.message}
          </p>
        )}
      </div>
    </Modal>
  );
}

/** Email isn't configured (or failed): hand the link to the admin to share. */
function SentModal({ sent, onClose }: { sent: SentInvite; onClose: () => void }) {
  return (
    <Modal
      open
      onClose={onClose}
      title="Share the invite link"
      description={`Email isn’t set up for invites, so send this link to ${sent.invite.email} yourself. It works once, for 7 days.`}
      footer={<Button onClick={onClose}>Done</Button>}
    >
      <CopyBlock label="Invite link" value={sent.link} />
    </Modal>
  );
}

/** Pending invites under the team list: role, expiry, resend (new link) and revoke. */
export function PendingInvites() {
  const { can } = useAuth();
  const toast = useToast();
  const invites = useInvites(can('read', 'User'));
  const resend = useResendInvite();
  const revoke = useRevokeInvite();
  const [shared, setShared] = useState<SentInvite | null>(null);
  const rows = invites.data ?? [];
  if (rows.length === 0) return null;

  return (
    <Card className="mt-5 overflow-hidden">
      <p className="flex items-center gap-2 border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-800">
        <UserPlus className="h-4 w-4 text-slate-400" /> Pending invites
        <Badge>{rows.length}</Badge>
      </p>
      <ul className="divide-y divide-slate-100">
        {rows.map((i) => (
          <li key={i.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-slate-800">{i.name || i.email}</span>
              <span className="block truncate text-xs text-slate-500">
                {i.name ? `${i.email} · ` : ''}expires {formatDate(i.expiresAt)}
              </span>
            </span>
            <Badge>{humanize(i.role)}</Badge>
            {can('create', 'User') && (
              <span className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  loading={resend.isPending && resend.variables === i.id}
                  onClick={() =>
                    resend.mutate(i.id, {
                      onSuccess: (res) =>
                        res.emailed
                          ? toast.success(`New invite sent to ${res.invite.email}`)
                          : setShared(res),
                      onError: (e) => toast.error(e.message),
                    })
                  }
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Resend
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Revoke invite for ${i.email}`}
                  loading={revoke.isPending && revoke.variables === i.id}
                  onClick={() =>
                    revoke.mutate(i.id, {
                      onSuccess: () => toast.success('Invite revoked'),
                      onError: (e) => toast.error(e.message),
                    })
                  }
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </span>
            )}
          </li>
        ))}
      </ul>
      {shared && <SentModal sent={shared} onClose={() => setShared(null)} />}
    </Card>
  );
}
